/**
 * Template Card Component
 *
 * Displays a single compliance template with metadata and actions.
 *
 * Standards: .equilateral-standards/frontend-development/frontend_standards.md
 */

import React from 'react';
import { Card, Button, Badge } from 'flowbite-react';
import {
  HiDocument,
  HiEye,
  HiDocumentAdd,
  HiShieldCheck
} from 'react-icons/hi';

interface ComplianceTemplate {
  Template_ID: string;
  Title: string;
  Category: string;
  Type: string;
  Compliance_Frameworks: string[];
  Source_File: string;
  Description: string;
}

interface TemplateCardProps {
  template: ComplianceTemplate;
  onSelect: () => void;
  onUse: () => void;
}

const TemplateCard: React.FC<TemplateCardProps> = ({ template, onSelect, onUse }) => {
  // Get color for framework badges
  const getFrameworkColor = (framework: string): string => {
    const colors: Record<string, string> = {
      'SOC2': 'blue',
      'ISO27001': 'purple',
      'GDPR': 'green',
      'CCPA': 'yellow'
    };
    return colors[framework] || 'gray';
  };

  // Get icon for category
  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'policies':
        return <HiShieldCheck className="text-blue-600" />;
      case 'procedures':
        return <HiDocument className="text-purple-600" />;
      case 'controls':
        return <HiShieldCheck className="text-green-600" />;
      default:
        return <HiDocument className="text-gray-600" />;
    }
  };

  // Format template type for display
  const formatType = (type: string): string => {
    return type
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <Card>
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex items-start gap-3 mb-3">
          <div className="text-2xl">
            {getCategoryIcon(template.Category)}
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold mb-1 line-clamp-2">
              {template.Title}
            </h3>
            <p className="text-sm text-gray-500">
              {formatType(template.Type)}
            </p>
          </div>
        </div>

        {/* Description */}
        <p className="text-sm text-gray-600 mb-4 line-clamp-3 flex-1">
          {template.Description}
        </p>

        {/* Compliance Frameworks */}
        <div className="mb-4">
          <div className="flex flex-wrap gap-2">
            {template.Compliance_Frameworks.map(framework => (
              <Badge
                key={framework}
                color={getFrameworkColor(framework) as any}
                size="sm"
              >
                {framework}
              </Badge>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-3 border-t border-gray-200">
          <Button
            size="sm"
            color="light"
            onClick={onSelect}
            className="flex-1"
          >
            <HiEye className="mr-2 h-4 w-4" />
            Preview
          </Button>
          <Button
            size="sm"
            onClick={onUse}
            className="flex-1"
          >
            <HiDocumentAdd className="mr-2 h-4 w-4" />
            Use Template
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default TemplateCard;
